/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useReducer, useState, useEffect, useRef, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { css, jsx, cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { DOCUMENT_SCROLL_ROOT_ID } from '@atlaskit/media-document-viewer/get-document-root';
import { DocumentViewer } from '@atlaskit/media-document-viewer/document-viewer';
import { useStaticCallback } from '@atlaskit/media-common/useStaticCallback';

import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { useMediaClient } from '@confluence/media-client/entry-points/useMediaClient.ts';
import {
	getMacroExperienceTracker,
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceFailure } from '@confluence/macro-tracker/entry-points/MacroExperienceFailure.ts';

import {
	parseViewPdfMacroParams,
	type ViewPdfMacroParams,
} from '../entry-points/viewPdfMacroParamsUtils.ts';

import { ZoomLevel } from './zoomLevel.ts';
import { useExportThumbnail } from './hooks/useExportThumbnail.ts';
import { useViewPdfData } from './hooks/useViewPdfData.ts';
import { i18n } from './viewPdfMacroI18n.ts';
import { ZoomToolbar, useZoomToolbarVisibility } from './ZoomToolbar.tsx';
import { FullscreenViewer } from './FullscreenViewer.tsx';

export const DEFAULT_VIEWER_WIDTH = '100%';
export const DEFAULT_VIEWER_HEIGHT = '650px';

export type ViewPdfMacroRendererProps = {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams?: ViewPdfMacroParams;
};

type State = {
	zoomLevel: ZoomLevel;
	isLoading: boolean;
	isFullscreen: boolean;
};

type Action =
	| { type: 'loading_completed' }
	| { type: 'set_zoom_level'; zoomLevel: ZoomLevel }
	| { type: 'toggle_fullscreen' };

const initialState: State = {
	zoomLevel: new ZoomLevel(1),
	isLoading: true,
	isFullscreen: false,
};

const reducer = (state: State, action: Action): State => {
	if (action.type === 'loading_completed') {
		return { ...state, isLoading: false };
	}
	if (action.type === 'set_zoom_level') {
		return { ...state, zoomLevel: action.zoomLevel };
	}
	if (action.type === 'toggle_fullscreen') {
		return { ...state, isFullscreen: !state.isFullscreen };
	}

	return state;
};

const styles = cssMap({
	thumbnailContainer: {
		position: 'relative',
		display: 'block',
	},
});

const documentScrollRootStyles = css({
	width: '100%',
	height: '100%',
	overflow: 'auto',
	position: 'relative',
});

const toError = (err: unknown): Error => {
	if (err instanceof Error) {
		return err;
	}

	const message = typeof err === 'string' ? err : 'Unknown error';
	return new Error(message);
};

const ViewPdfMacroRendererComponent = ({
	macroDefaultProps,
	macroParams,
}: ViewPdfMacroRendererProps) => {
	const { formatMessage } = useIntl();
	const [state, dispatch] = useReducer(reducer, initialState);
	const { extensionKey, mode, node } = macroDefaultProps;
	const trackingAttributes = macroDefaultProps.attributes ?? getMacroAttributesFromADFNode(node);
	const experienceName = getExperienceName(mode, node);

	const { attachmentName, pageName, spaceKey, date, viewerWidth, viewerHeight } =
		parseViewPdfMacroParams(macroParams);
	const contentId = macroDefaultProps?.contentId ?? null;

	// Generate a unique scroll root ID per macro instance by appending macroId.
	// This prevents DOM ID collisions when multiple viewpdf macros are rendered on the same page.
	const scrollRootId = macroDefaultProps?.attributes?.macroId
		? `${DOCUMENT_SCROLL_ROOT_ID}-${macroDefaultProps.attributes.macroId}`
		: DOCUMENT_SCROLL_ROOT_ID;

	const isDisplayMode = macroDefaultProps?.mode === 'renderer' || macroDefaultProps?.mode == null;
	const isEditorMode = mode === 'editor';

	const { mediaFileId, collectionName, sourceContentId, isLoading, error } = useViewPdfData({
		contentId,
		attachmentName,
		pageName,
		spaceKey,
		date,
	});

	const mediaClient = useMediaClient({
		contentId: sourceContentId ?? contentId ?? undefined,
		readOnly: true,
	});
	const [viewerError, setViewerError] = useState<Error | null>(null);
	const blobUrlsRef = useRef<Set<string>>(new Set());
	const hasTrackedSuccessRef = useRef(false);

	// Cleanup: revoke all created blob URLs on unmount to prevent memory leaks.
	useEffect(() => {
		const blobUrls = blobUrlsRef.current;
		return () => {
			blobUrls.forEach((blobUrl) => {
				URL.revokeObjectURL(blobUrl);
			});
			blobUrls.clear();
		};
	}, []);
	const thumbnailObjectUrl = useExportThumbnail({
		isDisplayMode,
		mediaClient,
		mediaFileId,
		collectionName,
	});

	const getContent = useStaticCallback(async (pageStart: number, pageEnd: number) => {
		if (!mediaClient || !mediaFileId) {
			const clientError = new Error('Media client or file ID not available');
			setViewerError(clientError);
			throw clientError;
		}
		try {
			return await mediaClient.mediaStore.getDocumentContent(mediaFileId, {
				pageStart,
				pageEnd,
				collectionName: collectionName ?? undefined,
			});
		} catch (fetchError) {
			const normalizedError = toError(fetchError);
			setViewerError(normalizedError);
			throw normalizedError;
		}
	});

	const getPageImageUrl = useStaticCallback(async (pageNumber: number, zoom: number) => {
		if (!mediaClient || !mediaFileId) {
			const clientError = new Error('Media client or file ID not available');
			setViewerError(clientError);
			throw clientError;
		}
		try {
			const blob = await mediaClient.mediaStore.getDocumentPageImage(mediaFileId, {
				page: pageNumber,
				zoom,
				collectionName: collectionName ?? undefined,
			});
			const newUrl = URL.createObjectURL(blob);
			blobUrlsRef.current.add(newUrl);
			return newUrl;
		} catch (fetchError) {
			const normalizedError = toError(fetchError);
			setViewerError(normalizedError);
			throw normalizedError;
		}
	});

	const onSuccess = useStaticCallback(() => {
		if (!hasTrackedSuccessRef.current) {
			getMacroExperienceTracker(mode, macroDefaultProps.contentId).succeed({
				name: experienceName,
				attributes: trackingAttributes,
			});
			hasTrackedSuccessRef.current = true;
		}
		dispatch({ type: 'loading_completed' });
	});

	const onZoomChange = useCallback((newZoomLevel: ZoomLevel) => {
		dispatch({ type: 'set_zoom_level', zoomLevel: newZoomLevel });
	}, []);

	const onFitToPage = useCallback(() => {
		dispatch({ type: 'set_zoom_level', zoomLevel: new ZoomLevel(1) });
	}, []);

	const onExpandToggle = useCallback(() => {
		dispatch({ type: 'toggle_fullscreen' });
	}, []);

	const { isVisible, onMouseEnter, onMouseLeave } = useZoomToolbarVisibility();
	const displayError = error ?? viewerError;

	if (displayError) {
		return (
			<React.Fragment>
				{!hasTrackedSuccessRef.current && (
					<MacroExperienceFailure
						name={experienceName}
						contentId={macroDefaultProps.contentId}
						extensionKey={extensionKey}
						mode={mode}
						attributes={trackingAttributes}
						error={displayError}
					/>
				)}
				<ErrorDisplay error={displayError} />
			</React.Fragment>
		);
	}

	if (!contentId) {
		const missingContentIdError = new Error(formatMessage(i18n.errorAttachmentNotFound));
		return (
			<React.Fragment>
				<MacroExperienceFailure
					name={experienceName}
					contentId={macroDefaultProps.contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={trackingAttributes}
					error={missingContentIdError}
				/>
				<ErrorDisplay error={missingContentIdError} />
			</React.Fragment>
		);
	}

	// Don't render a spinner here — it creates a UFO hold on VC90. Render null and let
	// DocumentViewer's overlay handle loading state. mediaClient must stay in the guard:
	// its absence causes getContent/getPageImageUrl to throw, triggering MacroExperienceFailure.
	if (isLoading || !mediaClient) {
		return null;
	}

	if (!mediaFileId) {
		const missingAttachmentError = new Error(formatMessage(i18n.errorAttachmentNotFound));
		return (
			<React.Fragment>
				<MacroExperienceFailure
					name={experienceName}
					contentId={macroDefaultProps.contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={trackingAttributes}
					error={missingAttachmentError}
				/>
				<ErrorDisplay error={missingAttachmentError} />
			</React.Fragment>
		);
	}

	// In export/email mode, show a thumbnail image fallback if one was generated.
	if (!isDisplayMode && thumbnailObjectUrl) {
		return (
			<React.Fragment>
				<Box xcss={styles.thumbnailContainer} testId="viewpdf-macro-thumbnail">
					<img src={thumbnailObjectUrl} alt={attachmentName ?? ''} />
				</Box>
			</React.Fragment>
		);
	}

	// Editor mode should not mount DocumentViewer; it is expensive and can hold VC90 open.
	if (isEditorMode) {
		return null;
	}

	// Display mode: render the Platform Media DocumentViewer.
	return (
		<div
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Dynamic values from macro params cannot be expressed as static CSS
			style={{ width: viewerWidth, height: viewerHeight, position: 'relative' }}
			data-testid="viewpdf-macro-viewer"
			data-fabric-macro={macroDefaultProps?.attributes?.macroId}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			onFocus={onMouseEnter}
			onBlur={onMouseLeave}
		>
			<div
				id={scrollRootId}
				data-testid="viewpdf-macro-scroll-root"
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Dynamic values from macro params cannot be expressed as static CSS
				style={{ opacity: state.isLoading ? 0 : 1 }}
				css={documentScrollRootStyles}
			>
				<DocumentViewer
					getContent={getContent}
					getPageImageUrl={getPageImageUrl}
					zoom={state.zoomLevel.value}
					onSuccess={onSuccess}
				/>
			</div>
			{!state.isLoading && isDisplayMode && (
				<ZoomToolbar
					zoomLevel={state.zoomLevel}
					onChange={onZoomChange}
					onFitToPage={onFitToPage}
					onExpandToggle={onExpandToggle}
					isFullscreen={state.isFullscreen}
					isVisible={isVisible}
				/>
			)}
			{state.isFullscreen && (
				<FullscreenViewer
					getContent={getContent}
					getPageImageUrl={getPageImageUrl}
					onClose={onExpandToggle}
				/>
			)}
		</div>
	);
};

const renderErrorFallback = () => <FormattedMessage {...i18n.error} />;

export const ViewPdfMacroRenderer = (props: ViewPdfMacroRendererProps): JSX.Element => {
	return (
		<GenericErrorBoundary
			renderOnError={renderErrorFallback}
			attribution={Attribution.PAGE_EXPERIENCES}
		>
			<ViewPdfMacroRendererComponent {...props} />
		</GenericErrorBoundary>
	);
};
